export interface NavCountBadgeProps {
  /** `undefined` while the count is still loading — renders nothing. */
  count?: number;
  /**
   * `error` — a filled red pill for a queue the parent can clear to zero
   * (Review). `neutral` — a quiet chip for an informational count that never
   * empties by design (Today).
   */
  tone?: 'error' | 'neutral';
  className?: string;
}

/** Three digits overflow the nav row; 99+ is the conventional ceiling. */
const MAX = 99;

const toneStyles: Record<string, string> = {
  // Unchanged from the hand-rolled span this replaces, so Review's existing
  // pill is byte-identical after the refactor.
  error: 'bg-danger-hover text-white',
  // Figma's neutral chip: default fill, subtle border, body text colour.
  neutral: 'bg-surface text-foreground border border-border',
};

/**
 * Count pill for a navigation row (YAR-399).
 *
 * Exists because the cap and the hide-at-zero rule are shared behaviour, and
 * the ceiling was previously absent: MobileDrawer and Sidebar each inlined
 * their own `count > 0 && <span>`, so a three-digit Review queue would have
 * pushed the nav row apart in both. BottomNavigation keeps its own overlay
 * pill — it is absolutely positioned over the icon rather than laid out in a
 * row, so it needs different geometry, and it is outside this ticket's scope.
 *
 * Not a variant of `Badge`: that primitive's tones are all light-fill +
 * coloured-text chips (`danger` is `bg-danger-light text-danger`), and the
 * nav pill is a solid fill. Bending `Badge` into a solid mode would change a
 * primitive used across every domain card for a nav-only need.
 */
export function NavCountBadge({
  count,
  tone = 'neutral',
  className = '',
}: NavCountBadgeProps) {
  // Zero is not a state worth a pill — an empty Review queue and a quiet day
  // both read better with nothing there than with a "0".
  if (count == null || count <= 0) return null;

  const label = count > MAX ? `${MAX}+` : String(count);

  return (
    <span
      // `ml-auto` pushes it to the end of the nav row, where all three navs
      // already placed their inline version.
      className={[
        // 24x24 per Figma's Badge; min-w-6 lets "99+" widen the pill
        // instead of squeezing it (YAR-476 / YAR-399 / YAR-128).
        'ml-auto shrink-0 inline-flex h-6 min-w-6 items-center justify-center px-1 text-center text-xs font-bold rounded-full',
        toneStyles[tone],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      // The digits alone are ambiguous once two rows carry a pill.
      // Approved copy: `[n] item` / `[n] items` (YAR-476).
      aria-label={`${label} ${count === 1 ? 'item' : 'items'}`}
    >
      {label}
    </span>
  );
}
